import React, { useState, useEffect } from 'react';
import { Flame, Award, Calendar, TrendingUp, Shield, Info } from 'lucide-react';
import { 
  getStreakStatus, 
  getStreakStats, 
  getStreakMessage,
  getStreakBadges 
} from '../../utils/streakManager';

const StreakTracker = ({ streakData, onShowCalendar }) => {
  const [stats, setStats] = useState({});
  const [status, setStatus] = useState({});
  const [message, setMessage] = useState({});
  const [badges, setBadges] = useState([]);

  useEffect(() => {
    if (streakData) {
      setStats(getStreakStats(streakData));
      setStatus(getStreakStatus(streakData));
      setMessage(getStreakMessage(streakData.currentStreak || 0));
      setBadges(getStreakBadges(streakData));
    }
  }, [streakData]);

  const currentStreak = streakData?.currentStreak || 0;

  return (
    <div className="streak-tracker">
      {/* Main Streak Display */}
      <div className="streak-main" style={{ borderColor: status.color }}>
        <div className="streak-flame">
          <Flame 
            size={60} 
            color={currentStreak > 0 ? '#e74c3c' : '#95a5a6'} 
            className={currentStreak > 0 ? 'animate-pulse' : ''}
          />
        </div>
        
        <div className="streak-count">
          <h2 style={{ color: status.color }}>{currentStreak}</h2>
          <p>Day Streak</p>
        </div>

        <div className="streak-status" style={{ background: status.color }}>
          <span className="status-emoji">{status.emoji}</span>
          <span className="status-message">{status.message}</span>
        </div>
      </div>

      {/* Motivational Message */}
      <div className="streak-motivation" style={{ borderLeftColor: message.color }}>
        <h3 style={{ color: message.color }}>{message.title}</h3>
        <p>{message.message}</p>
      </div>

      {/* Statistics Grid */}
      <div className="streak-stats-grid">
        <div className="streak-stat-card">
          <div className="stat-icon" style={{ background: '#f39c12' }}>
            <TrendingUp size={24} color="white" />
          </div>
          <div className="stat-content">
            <div className="stat-value">{stats.longestStreak || 0}</div>
            <div className="stat-label">Longest Streak</div>
          </div>
        </div>

        <div className="streak-stat-card">
          <div className="stat-icon" style={{ background: '#3498db' }}>
            <Calendar size={24} color="white" />
          </div>
          <div className="stat-content">
            <div className="stat-value">{stats.totalDaysPlayed || 0}</div>
            <div className="stat-label">Total Days</div>
          </div>
        </div>

        <div className="streak-stat-card">
          <div className="stat-icon" style={{ background: '#2ecc71' }}>
            <Award size={24} color="white" />
          </div>
          <div className="stat-content">
            <div className="stat-value">{stats.thisMonthDays || 0}</div>
            <div className="stat-label">This Month</div>
          </div>
        </div>

        <div className="streak-stat-card">
          <div className="stat-icon" style={{ background: stats.freezeAvailable ? '#9b59b6' : '#95a5a6' }}>
            <Shield size={24} color="white" />
          </div>
          <div className="stat-content">
            <div className="stat-value">{stats.freezeAvailable ? '✓' : '✗'}</div>
            <div className="stat-label">Freeze Available</div>
          </div>
        </div>
      </div>

      {/* Streak Freeze Info */}
      {stats.freezeAvailable && (
        <div className="streak-freeze-info">
          <Info size={16} />
          <span>Streak Freeze: Miss 1 day without breaking your streak!</span>
        </div>
      )}

      {/* Streak Badges */}
      {badges.length > 0 && (
        <div className="streak-badges-section">
          <h4>Streak Badges</h4>
          <div className="streak-badges-grid">
            {badges.map(badge => (
              <div 
                key={badge.id} 
                className={`streak-badge ${badge.earned ? 'earned' : 'past'}`}
                style={{ borderColor: badge.color }}
              >
                <div className="badge-icon" style={{ color: badge.color }}>
                  {badge.icon}
                </div>
                <div className="badge-name">{badge.name}</div>
                <div className="badge-desc">{badge.description}</div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* View Calendar Button */}
      <button 
        className="view-calendar-btn hover-lift"
        onClick={onShowCalendar}
      >
        <Calendar size={20} />
        View Activity Calendar
      </button>
    </div>
  );
};

export default StreakTracker;
